import { useState, useMemo, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, LifeBuoy, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import { type TurnstileInstance } from '@marsidev/react-turnstile';
import {
  createSupportTicket,
  type SupportTicketCategory,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { TurnstileGate } from '@/components/ui/TurnstileGate';
import styles from './SupportPage.module.css';

interface Props { onNavigate: (path: string) => void }

/**
 * Driver-facing support form — Phase 1 of the support-ticket system.
 *
 * Posts to POST /me/support, which writes a SupportTicket row + an
 * audit log entry. Returns a ticket id for the confirmation screen.
 *
 * Context handling
 * ────────────────
 * URL params populate optional context fields without driver effort:
 *
 *   /support?charger=<identity>&location=<id>  ← PreSessionPage deep-link
 *   /support?session=<id>                       ← ChargingPage deep-link
 *   /support                                    ← no context, general form
 *
 * The DriverAssistSheet's "Contact support" CTA constructs the deep-
 * links per surface (see PreSessionPage.tsx:759-769 and
 * ChargingPage.tsx:465-473). The contextSummary block at the top of
 * the form shows the driver what context the ticket will carry, so
 * they don't wonder whether ops will know what they were doing.
 *
 * Source attribution
 * ──────────────────
 * Tickets opened via the assistant's CTA carry `source: 'driver_assist'`;
 * tickets opened by typing /support directly carry `source: 'manual'`.
 * We infer from presence of URL params — if charger/session/location
 * are set, the driver arrived from the assistant. The distinction
 * matters for Phase 2's admin triage view ("assistant escalations"
 * may have different SLA expectations than cold tickets).
 *
 * Auth gate
 * ─────────
 * Routed inside App.tsx's authenticated-page switch — unauthenticated
 * users hit the login redirect before this component mounts. The
 * user's email is read from useAuthStore for the read-only "Replies
 * sent to" display, so the driver knows where to expect a response.
 */
export function SupportPage({ onNavigate }: Props) {
  const { t } = useTranslation('support');
  const user = useAuthStore((s) => s.user);

  // Parse URL params once on mount. The form keeps a static snapshot
  // of context — re-navigating with different params would require a
  // remount, which is the expected pattern for this surface.
  const urlContext = useMemo(() => {
    if (typeof window === 'undefined') {
      return { charger_identity: null, location_id: null, session_id: null };
    }
    const sp = new URLSearchParams(window.location.search);
    return {
      charger_identity: sp.get('charger'),
      location_id: sp.get('location'),
      session_id: sp.get('session'),
    };
  }, []);

  const hasAnyContext =
    Boolean(urlContext.charger_identity)
    || Boolean(urlContext.location_id)
    || Boolean(urlContext.session_id);

  const inferredSource: 'driver_assist' | 'manual' = hasAnyContext ? 'driver_assist' : 'manual';

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<SupportTicketCategory>(
    // Smart default: if the driver came from the assistant (which
    // is scoped to charge-start troubleshooting), pre-select that
    // category. Manual /support visits default to 'other'.
    hasAnyContext ? 'charge_start' : 'other',
  );

  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState<{ ticketId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // C4 Turnstile gate — token populated by widget on successful
  // challenge, cleared on error/expiry/server-side rejection.
  // canSubmit extends to require Boolean(turnstileToken) so the
  // Send button stays disabled until the captcha resolves.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const canSubmit =
    subject.trim().length > 0
    && description.trim().length > 0
    && !submitting
    && Boolean(turnstileToken);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !turnstileToken) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await createSupportTicket({
        subject: subject.trim(),
        description: description.trim(),
        category,
        source: inferredSource,
        context: {
          ...(urlContext.charger_identity ? { charger_identity: urlContext.charger_identity } : {}),
          ...(urlContext.location_id ? { location_id: urlContext.location_id } : {}),
          ...(urlContext.session_id ? { session_id: urlContext.session_id } : {}),
        },
        turnstile_token: turnstileToken,
      });
      setConfirmed({ ticketId: result.ticket_id });
    } catch (err) {
      // ky throws HTTPError on 4xx/5xx with the project's
      // {success:false, error:{code,message}} envelope in the body.
      let message = t('genericError');
      let errorCode: string | undefined;
      if (err instanceof Error && 'response' in err) {
        const httpErr = err as { response: Response };
        try {
          const body = await httpErr.response.json();
          if (body?.error?.message) message = body.error.message;
          if (body?.error?.code) errorCode = body.error.code;
        } catch {
          // Body wasn't JSON — keep the generic fallback.
        }
      }
      // Captcha-specific failures: reset the widget so a fresh
      // challenge can complete + clear the stale token. Otherwise
      // the Send button would stay enabled with a token the server
      // already rejected, and the user could only retry by reloading.
      if (errorCode === 'TURNSTILE_FAILED' || errorCode === 'TURNSTILE_REQUIRED') {
        turnstileRef.current?.reset();
        setTurnstileToken(null);
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Confirmation screen ─────────────────────────────────────────
  if (confirmed) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => onNavigate('/')}
            aria-label={t('backToHome')}
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className={styles.title}>{t('title')}</h1>
        </header>
        <div className={styles.confirmCard}>
          <div className={styles.confirmIcon}>
            <CheckCircle2 size={32} />
          </div>
          <h2 className={styles.confirmTitle}>{t('confirm.title')}</h2>
          <p className={styles.confirmBody}>
            {t('confirm.body', {
              ticketId: confirmed.ticketId.slice(-6).toUpperCase(),
              email: user?.email,
            })}
          </p>
          <button
            type="button"
            className={styles.confirmCta}
            onClick={() => onNavigate('/')}
          >
            {t('confirm.cta')}
          </button>
        </div>
      </div>
    );
  }

  // ── Form screen ────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => onNavigate('/')}
          aria-label={t('backToHome')}
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className={styles.title}>{t('title')}</h1>
      </header>

      <div className={styles.intro}>
        <div className={styles.introIcon}>
          <LifeBuoy size={20} />
        </div>
        <div>
          <p className={styles.introHeading}>
            {t('intro.heading')}
          </p>
          <p className={styles.introBody}>
            {t('intro.body')}
          </p>
        </div>
      </div>

      {hasAnyContext && (
        <div className={styles.contextCard}>
          <p className={styles.contextLabel}>{t('context.label')}</p>
          <ul className={styles.contextList}>
            {urlContext.charger_identity && (
              <li>{t('context.charger')} <code>{urlContext.charger_identity}</code></li>
            )}
            {urlContext.location_id && (
              <li>{t('context.location')}</li>
            )}
            {urlContext.session_id && (
              <li>{t('context.session')}</li>
            )}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('category.label')}</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as SupportTicketCategory)}
            className={styles.fieldSelect}
            disabled={submitting}
          >
            <option value="charge_start">{t('category.chargeStart')}</option>
            <option value="billing">{t('category.billing')}</option>
            <option value="account">{t('category.account')}</option>
            <option value="other">{t('category.other')}</option>
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('subject.label')}</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t('subject.placeholder')}
            maxLength={200}
            disabled={submitting}
            className={styles.fieldInput}
            required
          />
          <span className={styles.fieldHint}>{t('counter', { current: subject.length, max: 200 })}</span>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('description.label')}</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('description.placeholder')}
            maxLength={5000}
            disabled={submitting}
            className={styles.fieldTextarea}
            rows={6}
            required
          />
          <span className={styles.fieldHint}>{t('counter', { current: description.length, max: 5000 })}</span>
        </label>

        {/* C4 — Cloudflare Turnstile widget, via the shared
            TurnstileGate. Renders between the description textarea
            and the email note so it sits in the natural form scan
            order. Posture 'closed': with no site key baked in
            (mis-deploy / local dev with stale env) the fallback
            renders + the Send button stays disabled forever — fail
            closed is the right posture for the support gate. */}
        <TurnstileGate
          ref={turnstileRef}
          posture="closed"
          className={styles.turnstileWrap}
          onToken={setTurnstileToken}
          fallback={
            <div className={styles.errorCard} role="alert">
              <AlertTriangle size={14} />
              <span>{t('turnstileFallback')}</span>
            </div>
          }
        />

        {user?.email && (
          <p className={styles.emailNote}>
            {t('emailNote', { email: user.email })}
          </p>
        )}

        {error && (
          <div className={styles.errorCard} role="alert">
            <AlertTriangle size={14} />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className={styles.submitBtn}
        >
          {submitting ? (
            <>
              <Loader2 size={16} className={styles.spin} />
              <span>{t('submit.sending')}</span>
            </>
          ) : (
            <span>{t('submit.send')}</span>
          )}
        </button>
      </form>
    </div>
  );
}
